import React from 'react';
import SubpageNav from '../layout/SubpageNav';
import Footer from '../layout/Footer';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import Button from '../ui/Button';
import { colors, font } from '../../lib/tokens';
import { company } from '../../data/company';
import { teamMembers } from '../../data/team';
import { NEWS, newsPath } from '../../data/news';

const h2: React.CSSProperties = {
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 44,
  letterSpacing: -1.5,
  lineHeight: 1.02,
  color: colors.ink,
  margin: '16px 0 0',
};

const LOGOS = [
  {
    name: 'Peak mark',
    note: 'For light backgrounds',
    preview: '/press-kit/logo/katlacode-mark.svg',
    bg: colors.surface,
    files: [
      { label: 'SVG', href: '/press-kit/logo/katlacode-mark.svg' },
      { label: 'PNG', href: '/press-kit/logo/katlacode-mark.png' },
    ],
  },
  {
    name: 'Peak mark · inverse',
    note: 'For dark backgrounds',
    preview: '/press-kit/logo/katlacode-mark-inverse.svg',
    bg: colors.ink,
    files: [
      { label: 'SVG', href: '/press-kit/logo/katlacode-mark-inverse.svg' },
      { label: 'PNG', href: '/press-kit/logo/katlacode-mark-inverse.png' },
    ],
  },
  {
    name: 'Lockup',
    note: 'Mark and wordmark, light backgrounds',
    preview: '/press-kit/logo/katlacode-lockup.png',
    bg: colors.surface,
    wide: true,
    files: [{ label: 'PNG', href: '/press-kit/logo/katlacode-lockup.png' }],
  },
  {
    name: 'Lockup · inverse',
    note: 'Mark and wordmark, dark backgrounds',
    preview: '/press-kit/logo/katlacode-lockup-inverse.png',
    bg: colors.ink,
    wide: true,
    files: [{ label: 'PNG', href: '/press-kit/logo/katlacode-lockup-inverse.png' }],
  },
];

const TEAM_FILES: Record<string, { photo: string; illustration: string }> = {
  'Adeline Tracz': {
    photo: '/press-kit/team/adeline-tracz.jpg',
    illustration: '/press-kit/team/illustrations/adeline-tracz.png',
  },
  'Arnar Þór Guðjónsson': {
    photo: '/press-kit/team/arnar-thor-gudjonsson.jpg',
    illustration: '/press-kit/team/illustrations/arnar-thor-gudjonsson.png',
  },
  'Hafsteinn Einarsson': {
    photo: '/press-kit/team/hafsteinn-einarsson.png',
    illustration: '/press-kit/team/illustrations/hafsteinn-einarsson.png',
  },
  'Haraldur Orri Hauksson': {
    photo: '/press-kit/team/haraldur-orri-hauksson.png',
    illustration: '/press-kit/team/illustrations/haraldur-orri-hauksson.png',
  },
};

function DownloadLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      download
      className="kc-download-link"
      style={{
        fontFamily: font.mono,
        fontSize: 11,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {children} ↓
    </a>
  );
}

function Boilerplate({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.hair}`,
        borderRadius: 12,
        padding: '28px 32px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: colors.muted,
          }}
        >
          {label}
        </span>
        <button
          onClick={copy}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: copied ? colors.ink : colors.blue,
            padding: 0,
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p style={{ fontSize: 16, lineHeight: 1.65, color: colors.ink2, margin: 0 }}>{text}</p>
    </div>
  );
}

export default function PressKitPage() {
  React.useEffect(() => {
    document.title = 'Press kit · KatlaCode';
  }, []);

  return (
    <div className="min-h-screen bg-kc-bg text-kc-ink">
      <SubpageNav current="press" />

      {/* Intro */}
      <section style={{ background: colors.bg, padding: '96px 0 120px' }}>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
            <div>
              <Eyebrow>Press kit</Eyebrow>
              <h1 style={{ ...h2, fontSize: 'clamp(40px, 5vw, 56px)' }}>
                Writing about <span style={{ fontStyle: 'italic', color: colors.muted }}>katlacode</span>?
              </h1>
            </div>
            <div>
              <p style={{ fontSize: 18, lineHeight: 1.7, color: colors.ink2, margin: '8px 0 28px' }}>
                Company facts, approved logos, founder photography, and boilerplate you can
                publish verbatim. For rules on how the brand may be used, see the{' '}
                <a href="/brand" style={{ color: colors.blue }}>brand manual</a>.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Button variant="primary" href="/press-kit/katlacode-press-kit.zip">
                  Download full kit (ZIP)
                </Button>
                <Button href={`mailto:${company.email}`}>Press enquiries</Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Fast facts */}
      <section style={{ background: colors.surface, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
            <div>
              <Eyebrow>Fast facts</Eyebrow>
              <h2 style={h2}>The company at a glance</h2>
            </div>
            <dl style={{ margin: 0 }}>
              {company.facts.map((fact) => (
                <div
                  key={fact.label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '160px 1fr',
                    gap: 16,
                    padding: '14px 0',
                    borderBottom: `1px solid ${colors.hair2}`,
                  }}
                >
                  <dt
                    style={{
                      fontFamily: font.mono,
                      fontSize: 11,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: colors.muted,
                      paddingTop: 3,
                    }}
                  >
                    {fact.label}
                  </dt>
                  <dd style={{ margin: 0, fontSize: 16, color: colors.ink }}>
                    {fact.label === 'Press contact' ? (
                      <a href={`mailto:${fact.value}`} style={{ color: colors.blue, textDecoration: 'none' }}>
                        {fact.value}
                      </a>
                    ) : (
                      fact.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </Container>
      </section>

      {/* Boilerplate */}
      <section style={{ background: colors.bg, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
            <div>
              <Eyebrow>Boilerplate</Eyebrow>
              <h2 style={h2}>Describe us in your words, or ours</h2>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: colors.muted, marginTop: 20 }}>
                Both versions are approved for publication verbatim. In prose, the company is
                written <strong style={{ color: colors.ink, fontWeight: 600 }}>KatlaCode</strong>;
                only the wordmark itself is lowercase.
              </p>
            </div>
            <div style={{ display: 'grid', gap: 20 }}>
              <Boilerplate label="Short · one sentence" text={company.boilerplateShort} />
              <Boilerplate label="Long · about the company" text={company.boilerplateLong} />
            </div>
          </div>
        </Container>
      </section>

      {/* Logos */}
      <section style={{ background: colors.surface, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
        <Container>
          <Eyebrow>Logo downloads</Eyebrow>
          <h2 style={h2}>The peak mark</h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: colors.muted, margin: '20px 0 48px', maxWidth: 560 }}>
            Use the files as delivered. No recoloring, stretching, or effects. PNGs are
            high-resolution with transparent backgrounds; prefer SVG where supported.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {LOGOS.map((logo) => (
              <div
                key={logo.name}
                style={{
                  border: `1px solid ${colors.hair}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: logo.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 200,
                    padding: 32,
                  }}
                >
                  <img
                    src={logo.preview}
                    alt={`${logo.name} logo variant`}
                    loading="lazy"
                    decoding="async"
                    style={logo.wide ? { maxWidth: '70%', maxHeight: 90 } : { width: 96, height: 96 }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 16,
                    padding: '16px 20px',
                    borderTop: `1px solid ${colors.hair}`,
                    background: colors.surface,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>{logo.name}</div>
                    <div style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>{logo.note}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                    {logo.files.map((file) => (
                      <DownloadLink key={file.label} href={file.href}>
                        {file.label}
                      </DownloadLink>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Team */}
      <section style={{ background: colors.bg, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
        <Container>
          <Eyebrow>People</Eyebrow>
          <h2 style={h2}>Founders, in print quality</h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: colors.muted, margin: '20px 0 48px', maxWidth: 560 }}>
            Photographs and the illustrated portraits from our website are both approved for
            publication, credited to KatlaCode.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {teamMembers.map((member) => {
              const files = TEAM_FILES[member.name];
              return (
                <div key={member.name}>
                  <div
                    style={{
                      borderRadius: 12,
                      overflow: 'hidden',
                      border: `1px solid ${colors.hair}`,
                      background: colors.surface,
                      aspectRatio: '1 / 1',
                    }}
                  >
                    <img
                      src={files?.photo ?? member.photo}
                      alt={member.name}
                      loading="lazy"
                      decoding="async"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: colors.ink }}>{member.name}</div>
                    <div style={{ fontSize: 13, color: colors.muted, marginTop: 2 }}>{member.title}</div>
                    <div style={{ fontSize: 12, color: colors.dim, marginTop: 2 }}>{member.credentials}</div>
                    {files && (
                      <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                        <DownloadLink href={files.photo}>Photo</DownloadLink>
                        <DownloadLink href={files.illustration}>Portrait</DownloadLink>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Coverage */}
      <section style={{ background: colors.surface, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
            <div>
              <Eyebrow>In the news</Eyebrow>
              <h2 style={h2}>Recent announcements</h2>
            </div>
            <div>
              {NEWS.map((item) => (
                <div
                  key={item.slug}
                  style={{ padding: '20px 0', borderBottom: `1px solid ${colors.hair2}` }}
                >
                  <div
                    style={{
                      fontFamily: font.mono,
                      fontSize: 11,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: colors.muted,
                    }}
                  >
                    {item.date}
                  </div>
                  <a
                    href={newsPath(item.slug)}
                    style={{
                      display: 'block',
                      fontSize: 18,
                      fontWeight: 600,
                      color: colors.ink,
                      textDecoration: 'none',
                      marginTop: 6,
                    }}
                  >
                    {item.title}
                  </a>
                  <p style={{ fontSize: 14, lineHeight: 1.65, color: colors.muted, margin: '8px 0 0' }}>
                    {item.body}
                  </p>
                </div>
              ))}
              <p style={{ fontSize: 14, color: colors.muted, marginTop: 20 }}>
                For interviews, embargoed material, or anything else:{' '}
                <a href={`mailto:${company.email}`} style={{ color: colors.blue }}>
                  {company.email}
                </a>
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
